import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <img src="/assets/icon/valfin_logo.png" alt="Valfin" className="h-9 object-contain" />
          <Link
            to="/login"
            className="flex items-center space-x-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
          >
            <Icon name="ArrowLeft" size={14} />
            <span>Back to Login</span>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-10">

          {/* Title */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-foreground mb-1">Privacy Policy</h1>
            <p className="text-muted-foreground text-xs">Last updated: May 2025</p>
          </div>

          <div className="space-y-7 text-foreground">

            {/* Introduction */}
            <section>
              <p className="text-sm text-muted-foreground leading-relaxed">
                ValfinSurvey Pro is an internal field survey management application developed and
                operated by <strong className="text-foreground">Valfin</strong> for use exclusively
                by its authorised employees and surveyors. This Privacy Policy describes how data
                is collected, handled, and protected within the application. The app is not
                available to the general public and is used solely for conducting property
                valuations and field surveys on behalf of Valfin's clients.
              </p>
            </section>

            {/* 1 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">1.</span>
                <span>Who Uses This App</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                This application is used exclusively by <strong className="text-foreground">Valfin's internal surveyors and staff</strong>.
                Surveyors use the app to conduct on-site property inspections, record measurements,
                capture photographs, and submit survey reports. Access is restricted to authorised
                personnel only and requires a company-issued login.
              </p>
            </section>

            {/* 2 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">2.</span>
                <span>Data Collected During Surveys</span>
              </h2>
              <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                During the course of a field survey, the surveyor records the following information
                on behalf of Valfin:
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Customer Information:</strong> Name, contact person name, and phone number of the property owner or applicant.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Property Details:</strong> Address, property type, measurements, boundaries, setbacks, road information, floor details, and structural information.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Photographs:</strong> Images of the property interior, exterior, and surroundings captured using the device camera.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">GPS Location:</strong> Latitude and longitude coordinates of the surveyed property.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Rate &amp; Valuation Data:</strong> Market rates, surrounding features, and surveyor observations.</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                All data collected is entered by the surveyor as part of their official duties and
                is submitted directly to Valfin's secure backend system.
              </p>
            </section>

            {/* 3 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">3.</span>
                <span>Device Permissions Required</span>
              </h2>
              <p className="text-sm text-muted-foreground mb-2 leading-relaxed">
                The app requests the following device permissions to perform survey functions:
              </p>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Camera:</strong> To photograph the property and supporting documents during the survey. Photos are uploaded only when the surveyor submits the survey.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Location (GPS):</strong> To record the exact coordinates of the surveyed property. The application does not collect or track location data in the background when the app is closed or not actively being used for a survey.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Storage:</strong> To temporarily save survey data and photos on the device for offline use before syncing to the server.</span>
                </li>
              </ul>
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                These permissions are used solely for survey-related activities. No data is
                accessed in the background or outside of active survey sessions.
              </p>
            </section>

            {/* 4 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">4.</span>
                <span>How Data Is Used</span>
              </h2>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span>To generate property valuation reports and PDF data sheets for Valfin's clients (banks and financial institutions)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span>To maintain a centralised record of all field surveys conducted by Valfin</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span>To allow managers and administrators to review, track, and manage survey assignments</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span>To support internal reporting, analytics, and quality control</span>
                </li>
              </ul>
            </section>

            {/* 5 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">5.</span>
                <span>Data Access &amp; Sharing</span>
              </h2>
              <ul className="space-y-1.5 text-sm text-muted-foreground">
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Internal access only:</strong> Survey data is accessible only to authorised Valfin staff based on their assigned role (surveyor, coordinator, manager, admin).</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Client reports:</strong> Valuation reports generated from survey data may be shared with the relevant client (bank or financial institution) as part of Valfin's service delivery.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">No third-party sale:</strong> Survey data and customer information are never sold, rented, or shared with any third party for marketing or commercial purposes.</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Icon name="ChevronRight" size={14} className="mt-0.5 flex-shrink-0 text-primary" />
                  <span><strong className="text-foreground">Legal compliance:</strong> Data may be disclosed if required by law or a valid legal order.</span>
                </li>
              </ul>
            </section>

            {/* 6 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">6.</span>
                <span>Data Storage &amp; Security</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                All survey data is transmitted over encrypted HTTPS connections and stored on
                Valfin's secure backend servers. Access to the system is protected by
                authentication tokens and role-based access controls. Only authorised personnel
                can view or manage survey records. Temporary offline survey data stored on the
                device is securely synced to the backend server once internet connectivity
                is restored.
              </p>
            </section>

            {/* 7 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">7.</span>
                <span>Data Retention</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Survey records are retained for as long as required to fulfil Valfin's business
                and legal obligations. Employee account data is removed upon termination of
                employment or access revocation by an administrator.
              </p>
            </section>

            {/* 8 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">8.</span>
                <span>Changes to This Policy</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Valfin may update this Privacy Policy as the application evolves. Any significant
                changes will be communicated to authorised users. Continued use of the application
                after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            {/* 9 */}
            <section>
              <h2 className="text-base font-semibold text-foreground mb-2 flex items-center space-x-2">
                <span className="text-base font-bold text-primary flex-shrink-0">9.</span>
                <span>Contact</span>
              </h2>
              <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                For any questions regarding this Privacy Policy or data handling practices,
                please contact:
              </p>
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                <p className="text-sm font-semibold text-foreground">Valfin</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Email:{' '}
                  <a href="mailto:info@valfin.co.in" className="text-primary hover:underline">
                    info@valfin.co.in
                  </a>
                </p>
              </div>
            </section>

          </div>

          {/* Footer */}
          <div className="mt-8 pt-5 border-t border-border text-center">
            <p className="text-xs text-muted-foreground">
              © {new Date().getFullYear()} Valfin. All rights reserved.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center space-x-1 mt-2 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
            >
              <Icon name="ArrowLeft" size={13} />
              <span>Back to Login</span>
            </Link>
          </div>

        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
